import { PERM } from '#/config/permissions';
import { ROUTES } from '#/config/routes';
import { CUSTOMER_ORDER_STATUS_LABELS } from '#/modules/crmMgmt/customerOrder/helper/customerOrderStatus';
import { SALES_ORDER_STATUS_LABELS } from '#/modules/crmMgmt/salesOrder/helper/salesOrderStatus';
import { DOCUMENT_STATUS_LABELS } from '#/modules/imsMgmt/receipts/shared/documentStatus';
import { WORK_ORDER_STATUS_LABELS } from '#/modules/momMgmt/workOrder/helper/workOrderStatus';
import { MISC_PURCHASE_ORDER_STATUS_LABELS } from '#/modules/srmMgmt/miscPurchaseOrder/helper/miscPurchaseOrderStatus';
import { PURCHASE_ORDER_STATUS_LABELS } from '#/modules/srmMgmt/purchaseOrder/helper/purchaseOrderStatus';

/** 進貨來源 key → 顯示名稱（後端 `source_distribution.labels`） */
const RECEIPT_SOURCE_LABELS = {
  PURCHASE: '採購',
  MANUAL: '手動',
  WORK_ORDER: '工單',
};

/** 分布與金額桶共用：歷史資料無法判定時後端回 UNKNOWN */
export const DISTRIBUTION_UNKNOWN_LABEL = '未知';

/** 金額桶 key → 顯示名稱（後端 `amounts.keys`；缺 key 時退回 `labels`） */
export const AMOUNT_KEY_LABELS = {
  UNINVOICED: '未開票',
  UNPAID: '未收款',
  PAID: '已收款',
  OPEN: '未結案',
  CLOSED: '已結案',
};

/** 金額桶 key → 固定配色序號（getSeriesColor）；未列出的 key 依順序取色。未結案用橘、已結案用藍。 */
export const AMOUNT_KEY_COLOR_INDEX = {
  OPEN: 1,
  CLOSED: 0,
};

/**
 * 主控台各 module 設定（對齊 backend-docs dashboard 交接 261013／261014／261015）。
 *
 * - `backlog`：「目前待辦」卡（response `backlog`，不受日期限制）；鑽取只帶 status（可逗號 OR）。
 *   `overdue` 為逾期卡的額外鑽取條件：plannedShip（planned_ship_date_to＝昨天）、unreceived（overdue_unreceived=true）、
 *   scheduledStart（scheduled_start_date_to＝昨天）。`tone` 為狀態語意色（見 DASHBOARD_TONES）。
 *   沒有 `status` 的卡只顯示筆數、不可鑽取（如工單缺料預警：後端只給筆數，無對應列表篩選），`hint` 為滑過說明。
 * - `completion`：「完成」卡（response `completion`，依完成事件日期＝頁面日期區間）；鑽取帶 status＋completion_date_from/to。
 * - `distribution`（選填）：單據期間內的分布（進貨來源、出庫類別），`labels` 為 key → 顯示名稱。
 * - `statusOrder`：月圖 series 的完整 enum 順序（同色系內的深淺依此順序固定，series 被裁切時不重新上色）。
 * - `statusTones`：月圖每個狀態的語意色（與狀態卡同一套 DASHBOARD_TONES）。
 * - `phases`：月別圖為「各月進度」橫向 100% 進度條，狀態依階段合併成段；
 *   response 沒有任何成員狀態的階段（如無財務權限時銷貨單的待開票／待收款）整段不顯示。
 * - `hasAmounts`：overview 可能回 `amounts`（有財務可見性才有）。
 */
export const DASHBOARD_MODULES = [
  {
    key: 'salesOrder',
    title: '銷貨單',
    permission: PERM.SALES_ORDER_READ,
    listRoute: ROUTES.salesOrder,
    hasAmounts: true,
    statusLabels: SALES_ORDER_STATUS_LABELS,
    statusOrder: ['DRAFT', 'PREPARING', 'SHIPPING', 'DELIVERED', 'INVOICED', 'PAID', 'CANCELLED'],
    phases: [
      { key: 'draft', label: '草稿', tone: 'pending', statuses: ['DRAFT'] },
      { key: 'shipping', label: '出貨中', tone: 'active', statuses: ['PREPARING', 'SHIPPING'] },
      { key: 'pendingInvoice', label: '待開票', tone: 'warn', statuses: ['DELIVERED'] },
      { key: 'pendingPayment', label: '待收款', tone: 'warn', statuses: ['INVOICED'] },
      { key: 'paid', label: '已收款', tone: 'done', statuses: ['PAID'] },
      { key: 'cancelled', label: '已取消', tone: 'alert', statuses: ['CANCELLED'] },
    ],
    statusTones: {
      DRAFT: 'pending',
      PREPARING: 'active',
      SHIPPING: 'active',
      DELIVERED: 'warn',
      INVOICED: 'warn',
      PAID: 'done',
      CANCELLED: 'alert',
    },
    backlog: [
      { key: 'draft', label: '草稿', status: 'DRAFT', tone: 'pending' },
      { key: 'pendingShipment', label: '待出貨', status: 'PREPARING', tone: 'active' },
      { key: 'overdueUnshipped', label: '逾期未出貨', status: 'PREPARING', overdue: 'plannedShip', tone: 'alert' },
      { key: 'shipping', label: '出貨中', status: 'SHIPPING', tone: 'active' },
      { key: 'pendingInvoice', label: '待開票', status: 'DELIVERED', tone: 'warn' },
      { key: 'pendingPayment', label: '待收款', status: 'INVOICED', tone: 'warn' },
    ],
    completion: [],
  },
  {
    key: 'customerOrder',
    title: '客戶訂單',
    permission: PERM.CUSTOMER_ORDER_READ,
    listRoute: ROUTES.customerOrder,
    hasAmounts: true,
    statusLabels: CUSTOMER_ORDER_STATUS_LABELS,
    statusOrder: ['DRAFT', 'SUBMITTED', 'APPROVED', 'PARTIALLY_SHIPPED', 'SHIPPED', 'CLOSED', 'TERMINATED'],
    phases: [
      { key: 'notEffective', label: '未生效', tone: 'warn', statuses: ['DRAFT', 'SUBMITTED'] },
      { key: 'delivering', label: '交付中', tone: 'active', statuses: ['APPROVED', 'PARTIALLY_SHIPPED'] },
      { key: 'delivered', label: '已交付', tone: 'done', statuses: ['SHIPPED', 'CLOSED'] },
      { key: 'terminated', label: '已中止', tone: 'alert', statuses: ['TERMINATED'] },
    ],
    statusTones: {
      DRAFT: 'pending',
      SUBMITTED: 'warn',
      APPROVED: 'active',
      PARTIALLY_SHIPPED: 'active',
      SHIPPED: 'done',
      CLOSED: 'done',
      TERMINATED: 'alert',
    },
    backlog: [
      { key: 'draft', label: '草稿', status: 'DRAFT', tone: 'pending' },
      { key: 'pendingReview', label: '待審核', status: 'SUBMITTED', tone: 'warn' },
      { key: 'approvedPendingDelivery', label: '已核准待交付', status: 'APPROVED', tone: 'active' },
      { key: 'partiallyDelivered', label: '部分交付', status: 'PARTIALLY_SHIPPED', tone: 'active' },
      { key: 'fullyDeliveredOpen', label: '已交付待結案', status: 'SHIPPED', tone: 'done' },
    ],
    completion: [],
  },
  {
    key: 'purchaseOrder',
    title: '物料採購',
    permission: PERM.PURCHASE_ORDER_READ,
    listRoute: ROUTES.purchaseOrder,
    hasAmounts: true,
    statusLabels: PURCHASE_ORDER_STATUS_LABELS,
    statusOrder: ['DRAFT', 'CANCELLED', 'SUBMITTED', 'APPROVED', 'PARTIALLY_RECEIVED', 'RECEIVED', 'CLOSED'],
    phases: [
      { key: 'notEffective', label: '未生效', tone: 'warn', statuses: ['DRAFT', 'SUBMITTED'] },
      { key: 'receiving', label: '收貨中', tone: 'active', statuses: ['APPROVED', 'PARTIALLY_RECEIVED'] },
      { key: 'received', label: '已收貨', tone: 'done', statuses: ['RECEIVED', 'CLOSED'] },
      { key: 'cancelled', label: '已取消', tone: 'alert', statuses: ['CANCELLED'] },
    ],
    statusTones: {
      DRAFT: 'pending',
      CANCELLED: 'alert',
      SUBMITTED: 'warn',
      APPROVED: 'active',
      PARTIALLY_RECEIVED: 'active',
      RECEIVED: 'done',
      CLOSED: 'done',
    },
    backlog: [
      { key: 'draft', label: '草稿', status: 'DRAFT', tone: 'pending' },
      { key: 'pendingReview', label: '待審核', status: 'SUBMITTED', tone: 'warn' },
      { key: 'approvedPendingReceipt', label: '已核准待收貨', status: 'APPROVED', tone: 'active' },
      { key: 'partiallyReceived', label: '部分收貨', status: 'PARTIALLY_RECEIVED', tone: 'active' },
      {
        key: 'overdueUnreceived',
        label: '逾期未到貨',
        status: 'APPROVED,PARTIALLY_RECEIVED',
        overdue: 'unreceived',
        tone: 'alert',
      },
    ],
    completion: [
      { key: 'received', label: '全部收貨', status: 'RECEIVED', tone: 'done' },
    ],
  },
  {
    key: 'miscPurchaseOrder',
    title: '非庫存採購',
    permission: PERM.MISC_PURCHASE_ORDER_READ,
    listRoute: ROUTES.miscPurchaseOrder,
    hasAmounts: true,
    statusLabels: MISC_PURCHASE_ORDER_STATUS_LABELS,
    statusOrder: ['DRAFT', 'CANCELLED', 'SUBMITTED', 'APPROVED', 'CLOSED'],
    phases: [
      { key: 'notEffective', label: '未生效', tone: 'warn', statuses: ['DRAFT', 'SUBMITTED'] },
      { key: 'executing', label: '執行中', tone: 'active', statuses: ['APPROVED'] },
      { key: 'closed', label: '已結案', tone: 'done', statuses: ['CLOSED'] },
      { key: 'cancelled', label: '已取消', tone: 'alert', statuses: ['CANCELLED'] },
    ],
    statusTones: {
      DRAFT: 'pending',
      CANCELLED: 'alert',
      SUBMITTED: 'warn',
      APPROVED: 'active',
      CLOSED: 'done',
    },
    backlog: [
      { key: 'draft', label: '草稿', status: 'DRAFT', tone: 'pending' },
      { key: 'submitted', label: '待審核', status: 'SUBMITTED', tone: 'warn' },
      { key: 'approved', label: '已核准未結案', status: 'APPROVED', tone: 'active' },
    ],
    completion: [
      { key: 'completed', label: '已結案', status: 'CLOSED', tone: 'done' },
    ],
  },
  {
    key: 'stockReceipt',
    title: '進貨單',
    permission: PERM.STOCK_RECEIPT_READ,
    listRoute: ROUTES.stockReceipt,
    hasAmounts: true,
    statusLabels: DOCUMENT_STATUS_LABELS,
    statusOrder: ['DRAFT', 'SUBMITTED', 'COMPLETED'],
    phases: [
      { key: 'draft', label: '草稿', tone: 'pending', statuses: ['DRAFT'] },
      { key: 'pendingReview', label: '待審核', tone: 'warn', statuses: ['SUBMITTED'] },
      { key: 'completed', label: '已完成', tone: 'done', statuses: ['COMPLETED'] },
    ],
    statusTones: { DRAFT: 'pending', SUBMITTED: 'warn', COMPLETED: 'done' },
    backlog: [
      { key: 'draft', label: '草稿', status: 'DRAFT', tone: 'pending' },
      { key: 'pendingReview', label: '待審核', status: 'SUBMITTED', tone: 'warn' },
    ],
    completion: [
      { key: 'completed', label: '已完成', status: 'COMPLETED', tone: 'done' },
    ],
    distribution: { key: 'sourceDistribution', title: '進貨來源', labels: RECEIPT_SOURCE_LABELS },
  },
  {
    key: 'stockIssue',
    title: '出庫單',
    permission: PERM.STOCK_ISSUE_READ,
    listRoute: ROUTES.stockIssue,
    hasAmounts: false,
    statusLabels: DOCUMENT_STATUS_LABELS,
    statusOrder: ['DRAFT', 'SUBMITTED', 'COMPLETED'],
    phases: [
      { key: 'draft', label: '草稿', tone: 'pending', statuses: ['DRAFT'] },
      { key: 'pendingReview', label: '待審核', tone: 'warn', statuses: ['SUBMITTED'] },
      { key: 'completed', label: '已完成', tone: 'done', statuses: ['COMPLETED'] },
    ],
    statusTones: { DRAFT: 'pending', SUBMITTED: 'warn', COMPLETED: 'done' },
    backlog: [
      { key: 'draft', label: '草稿', status: 'DRAFT', tone: 'pending' },
      { key: 'pendingReview', label: '待審核', status: 'SUBMITTED', tone: 'warn' },
    ],
    completion: [
      { key: 'completed', label: '已完成', status: 'COMPLETED', tone: 'done' },
    ],
    distribution: { key: 'categoryDistribution', title: '出庫類別', labels: {} },
  },
  {
    key: 'workOrder',
    title: '工單',
    permission: PERM.WORK_ORDER_READ,
    listRoute: ROUTES.workOrder,
    hasAmounts: false,
    statusLabels: WORK_ORDER_STATUS_LABELS,
    statusOrder: ['DRAFT', 'SUBMITTED', 'PROCESSING', 'CLOSED', 'ABORTED'],
    phases: [
      { key: 'notEffective', label: '未生效', tone: 'warn', statuses: ['DRAFT', 'SUBMITTED'] },
      { key: 'processing', label: '生產中', tone: 'active', statuses: ['PROCESSING'] },
      { key: 'closed', label: '已結案', tone: 'done', statuses: ['CLOSED'] },
      { key: 'aborted', label: '已中止', tone: 'alert', statuses: ['ABORTED'] },
    ],
    statusTones: {
      DRAFT: 'pending',
      SUBMITTED: 'warn',
      PROCESSING: 'active',
      CLOSED: 'done',
      ABORTED: 'alert',
    },
    backlog: [
      { key: 'draft', label: '草稿', status: 'DRAFT', tone: 'pending' },
      { key: 'submitted', label: '待審核', status: 'SUBMITTED', tone: 'warn' },
      { key: 'overdueNotStarted', label: '逾期未開工', status: 'SUBMITTED', overdue: 'scheduledStart', tone: 'alert' },
      { key: 'processing', label: '生產中', status: 'PROCESSING', tone: 'active' },
      {
        key: 'materialShortageAlert',
        label: '缺料預警',
        tone: 'alert',
        hint: '生產中、14 天內開工且物料分析為風險／缺料的工單；明細請到 生產製造 › 分析',
      },
    ],
    completion: [
      { key: 'closed', label: '已結案', status: 'CLOSED', tone: 'done' },
      { key: 'aborted', label: '已中止', status: 'ABORTED', tone: 'alert' },
    ],
  },
];

export const DASHBOARD_MODULE_MAP = Object.fromEntries(
  DASHBOARD_MODULES.map((config) => [config.key, config]),
);

/**
 * 狀態語意色：狀態卡整塊底色＋圖例，讓使用者快速找到要處理的區塊（文字仍用一般字色）。
 * 淺色用 100 階底＋300 階框、深色用 500/25 透明底＋500/60 框，兩種主題皆可讀。
 */
export const DASHBOARD_TONES = {
  pending: {
    label: '草稿',
    tileClass: 'border-gray-300 bg-gray-200 hover:bg-gray-300 dark:border-border dark:bg-muted dark:hover:bg-muted/70',
    dotClass: 'bg-gray-400',
  },
  warn: {
    label: '待處理',
    tileClass: `
      border-amber-300 bg-amber-100 hover:bg-amber-200
      dark:border-amber-500/60 dark:bg-amber-500/25 dark:hover:bg-amber-500/35
    `,
    dotClass: 'bg-amber-500',
  },
  active: {
    label: '進行中',
    tileClass: `
      border-sky-300 bg-sky-100 hover:bg-sky-200
      dark:border-sky-500/60 dark:bg-sky-500/25 dark:hover:bg-sky-500/35
    `,
    dotClass: 'bg-sky-500',
  },
  done: {
    label: '完成',
    tileClass: `
      border-emerald-300 bg-emerald-100 hover:bg-emerald-200
      dark:border-emerald-500/60 dark:bg-emerald-500/25 dark:hover:bg-emerald-500/35
    `,
    dotClass: 'bg-emerald-500',
  },
  alert: {
    label: '異常',
    tileClass: `
      border-red-300 bg-red-100 hover:bg-red-200
      dark:border-red-500/60 dark:bg-red-500/25 dark:hover:bg-red-500/35
    `,
    dotClass: 'bg-red-500',
  },
};

/**
 * 功能分組：標題下方的分類 tabs；切換時同時切換狀態卡與圖表。`moduleKeys` 順序即組內顯示順序。
 * `scrollX`：狀態卡區塊單列不換行，超出寬度時橫向捲動（物料有三塊）。
 */
export const DASHBOARD_GROUPS = [
  { key: 'customer', title: '客戶', moduleKeys: ['customerOrder', 'salesOrder'] },
  { key: 'material', title: '物料', moduleKeys: ['purchaseOrder', 'stockReceipt', 'miscPurchaseOrder'], scrollX: true },
  { key: 'production', title: '生管', moduleKeys: ['workOrder', 'stockIssue'] },
];
